import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SelectionListPlaceholder } from './SelectionListPlaceholder';
import { TextMarkPlaceholder } from './TextMarkPlaceholder';
import { selectionListToken, textMarkToken } from './placeholderTokens';
import {
  SELECTION_LIST_PLACEHOLDER,
  TEXT_MARK_PLACEHOLDER,
  type SelectionFlag,
} from './placeholderTypes';

export interface PlaceholderExtensionOptions {
  onActivateList: (pos: number) => void;
  onAdvance: (fromPos: number) => boolean;
  onRetreat: (fromPos: number) => boolean;
  consumeActivation: (pos: number) => boolean;
}

type SelectionListNodeOptions = Pick<PlaceholderExtensionOptions, 'onActivateList'>;
type TextMarkNodeOptions = Pick<
  PlaceholderExtensionOptions,
  'onAdvance' | 'onRetreat' | 'consumeActivation'
>;

const SelectionListPlaceholderNode = Node.create<SelectionListNodeOptions>({
  name: SELECTION_LIST_PLACEHOLDER,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onActivateList: () => {} };
  },

  addAttributes() {
    return {
      flag: { default: 'E' as SelectionFlag },
      name: { default: '' },
      skipped: { default: false, rendered: false },
    };
  },

  // Serialize to a <span> wrapping the [[F:Name]] token string, so saved HTML carries clean
  // tokens. On reload the base rich-text extensions drop the span and the inner token text
  // re-tokenizes via toBuildingBlockDoc, not parseHTML.
  renderHTML({ node }) {
    return [
      'span',
      {},
      selectionListToken(node.attrs.flag as SelectionFlag, node.attrs.name as string),
    ];
  },

  renderText({ node }) {
    return selectionListToken(node.attrs.flag as SelectionFlag, node.attrs.name as string);
  },

  addNodeView() {
    return ReactNodeViewRenderer(SelectionListPlaceholder, { trackNodeViewPosition: true });
  },
});

const TextMarkPlaceholderNode = Node.create<TextMarkNodeOptions>({
  name: TEXT_MARK_PLACEHOLDER,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { onAdvance: () => false, onRetreat: () => false, consumeActivation: () => false };
  },

  addAttributes() {
    return {
      name: { default: '' },
      value: { default: '' },
    };
  },

  // Serialize to a <span> wrapping either the [[T:Name]] token (empty, still unresolved)
  // or the typed value (filled, resolved plain text that will not be re-wrapped on reload).
  renderHTML({ node }) {
    const value = node.attrs.value as string;
    return ['span', {}, value.length > 0 ? value : textMarkToken(node.attrs.name as string)];
  },

  renderText({ node }) {
    const value = node.attrs.value as string;
    return value.length > 0 ? value : textMarkToken(node.attrs.name as string);
  },

  addNodeView() {
    return ReactNodeViewRenderer(TextMarkPlaceholder, { trackNodeViewPosition: true });
  },
});

export function placeholderExtensions(options: PlaceholderExtensionOptions): Node[] {
  return [
    SelectionListPlaceholderNode.configure(options),
    TextMarkPlaceholderNode.configure(options),
  ];
}

export { SelectionListPlaceholderNode, TextMarkPlaceholderNode };
